<template>
    <div class="ds-dossier">
        <!-- Досье -->
        <div v-if="screen === 'dossier'" class="ds-dossier-body">
            <div class="ds-dossier-head">
                <img v-if="person.avatar && !avatarFailed" :src="person.avatar" alt="" class="ds-dossier-avatar" @error="avatarFailed = true">
                <span v-else class="ds-dossier-avatar" aria-hidden="true">{{ initials }}</span>
                <div class="ds-dossier-head-text">
                    <span class="ds-dossier-name">{{ displayName }}</span>
                    <span class="ds-dossier-muted">{{ roleLine }}</span>
                    <span v-if="tenureLine" class="ds-dossier-muted">{{ tenureLine }}</span>
                </div>
                <div v-if="menuItems.length" ref="menuWrap" class="ds-dossier-menu-wrap">
                    <button
                        type="button"
                        class="ds-dossier-menu-button"
                        aria-label="Действия"
                        aria-haspopup="menu"
                        :aria-expanded="isMenuOpen"
                        title="Изменить данные, трудоустройство, доступ"
                        @click="isMenuOpen = !isMenuOpen"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
                    </button>
                    <div v-if="isMenuOpen" class="ds-dossier-menu ds-glass-menu" role="menu" @keydown.escape="isMenuOpen = false">
                        <template v-for="item in menuItems" :key="item.id">
                            <div v-if="item.divider" class="ds-glass-menu-divider" aria-hidden="true"></div>
                            <button
                                type="button"
                                role="menuitem"
                                class="ds-glass-menu-item"
                                :class="{ 'ds-glass-menu-item-danger': item.danger }"
                                @click="runMenu(item.id)"
                            ><span class="ds-glass-menu-item-label">{{ item.label }}</span></button>
                        </template>
                    </div>
                </div>
            </div>

            <div class="ds-dossier-contacts">
                <a v-if="person.phone" :href="`tel:${person.phone}`" class="ds-dossier-phone">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>
                    {{ person.phone }}
                </a>
                <span v-else class="ds-dossier-email is-empty">телефон {{ DASH }}</span>
                <span class="ds-dossier-email" :class="{ 'is-empty': !person.email }">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
                    {{ person.email || 'e-mail не указан' }}
                </span>
                <span v-if="person.smartCheckin" class="ds-dossier-pill ds-dossier-pill-smart">СмартОтметка включена</span>
            </div>

            <div v-if="data.userStatus.value === 'error'" class="ds-dossier-state" role="alert">
                <span>Не удалось обновить данные сотрудника</span>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="data.reloadUser()">Повторить</button>
            </div>

            <template v-if="selected">
                <div class="ds-dossier-tabs" role="tablist">
                    <button
                        v-for="tab in tabs"
                        :key="tab.id"
                        type="button"
                        role="tab"
                        class="ds-dossier-tab"
                        :aria-selected="activeTab === tab.id"
                        @click="activeTab = tab.id"
                    >{{ tab.label }}</button>
                </div>

                <DossierSummary
                    v-if="activeTab === 'summary'"
                    :data
                    :employment="selected"
                    :others="otherEmployments"
                    :archive="viewOnly"
                    :month="summaryMonth"
                    :today
                    :now
                />
                <DossierShifts
                    v-else-if="activeTab === 'shifts'"
                    v-model:week-start="weekStart"
                    :data
                    :employment="selected"
                    :today
                    :now
                    @open-discipline="openDisciplineMonth"
                />
                <DossierDiscipline
                    v-else-if="activeTab === 'discipline'"
                    v-model:period="disciplinePeriod"
                    v-model:month="disciplineMonth"
                    :data
                    :anchor="summaryMonth"
                />
                <DossierExchange
                    v-else-if="activeTab === 'exchange'"
                    v-model:period="exchangePeriod"
                    :data
                    :anchor="anchorDay"
                    :end="exchangeEnd"
                    @open-responses="screen = 'responses'"
                />
            </template>
            <div v-else-if="data.userStatus.value === 'loading'" class="ds-dossier-skeleton" style="height: 240px" aria-busy="true"></div>
            <div v-else class="ds-dossier-state">Нет трудоустройств</div>
        </div>

        <!-- Подэкраны. «Личные данные» остаются смонтированными под подэкранами ТУ: draft User не теряется. -->
        <div v-if="personalMounted" v-show="screen === 'personal'" class="ds-dossier-screen">
            <DossierPersonalForm
                :person
                :employments="data.employments.value"
                :main-id="viewOnly ? null : selected?.id ?? null"
                :today-key="todayKey"
                :focus="editFocus"
                :can-edit-user="canWrite && data.permissions.value.editUser"
                :can-change-smart="canWrite && data.permissions.value.changeSmart"
                :can-edit-employment="canWrite && data.permissions.value.editEmployment"
                :can-create-employment="canWrite && data.permissions.value.createEmployment"
                :back-label="backLabel"
                :after-write="afterWrite"
                :reload-user="data.reloadUser"
                @back="backToDossier"
                @saved="onPersonalSaved"
                @open-employment="openEmployment"
                @new-employment="screen = 'new-employment'"
            />
        </div>
        <DossierEmploymentScreen
            v-if="canWrite && screen === 'employment' && editingEmployment"
            :key="editingEmployment.id"
            :person
            :employment="editingEmployment"
            :today
            :after-write="afterWrite"
            :reload-user="data.reloadUser"
            @back="screen = 'personal'"
            @done="returnToPersonal"
        />
        <DossierNewEmployment
            v-else-if="canWrite && screen === 'new-employment'"
            :person
            :employments="data.employments.value"
            :today-key="todayKey"
            :after-write="afterWrite"
            @back="screen = 'personal'"
            @done="returnToPersonal"
        />
        <DossierPassword
            v-else-if="canWrite && screen === 'password'"
            :person
            :back-label="backLabel"
            @back="backToDossier"
            @saved="onPasswordSaved"
        />
        <DossierResponses
            v-else-if="screen === 'responses' && selected"
            v-model:period="exchangePeriod"
            :data
            :anchor="anchorDay"
            :end="exchangeEnd"
            :back-label="backLabel"
            @back="backToDossier"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { useToast } from 'vue-toast-notification'
import type { Schedule } from '@/types'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useWallClockNow, weekStartOf } from '@/components/schedule/weekSchedule'
import {
    type DisciplinePeriod,
    type DossierContext,
    type DossierEmployment,
    type DossierScreen,
    type DossierTab,
    type EditFocus,
    DASH,
    DATE_KEY,
    formatDate,
    formatTenure,
    fullName,
    isActiveEmployment,
    isEndedEmployment,
    normalizePerson,
    normalizeScheduleRow,
    normalizeUserEmployments,
    pickArchiveEmployment,
    pickMainEmployment,
    shortName,
} from './dossierModel'
import type { ExchangePeriod } from './dossierActivity'
import { useDossierData } from './useDossierData'
import DossierSummary from './DossierSummary.vue'
import DossierShifts from './DossierShifts.vue'
import DossierDiscipline from './DossierDiscipline.vue'
import DossierExchange from './DossierExchange.vue'
import DossierResponses from './DossierResponses.vue'
import DossierPersonalForm from './DossierPersonalForm.vue'
import DossierEmploymentScreen from './DossierEmploymentScreen.vue'
import DossierNewEmployment from './DossierNewEmployment.vue'
import DossierPassword from './DossierPassword.vue'

// Единое досье C (RD-149 r2). context=schedule — только ТУ строки графика (подразделение открытия), без меню
// и изменений, независимо от is_main. employees — шапка и метрики основного ТУ; archive — только просмотр
// ТУ с последней датой увольнения. Все ТУ редактируются через «Личные данные» (вариант B).
const props = withDefaults(defineProps<{
    context: DossierContext
    archive?: boolean
    // employees: известная запись строки списка/User; schedule: строка графика (id — Employment UUID).
    user?: Record<string, unknown> | null
    schedule?: Schedule | null
}>(), {
    archive: false,
    user: null,
    schedule: null,
})

// updated — подтверждённая запись (данные списка могли измениться).
const emit = defineEmits<{ updated: [] }>()

const toast = useToast({ position: 'bottom-right' })
const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const directories = { departments: departmentStore.departments, positions: scheduleStore.positions }

const scheduleRow = props.context === 'schedule' && props.schedule ? normalizeScheduleRow(props.schedule, directories) : null
const fixedEmploymentId = scheduleRow?.employment.id ?? null
const data = useDossierData(scheduleRow
    ? { person: scheduleRow.person, employments: [scheduleRow.employment] }
    : { person: normalizePerson(props.user), employments: normalizeUserEmployments(props.user?.employments, directories, false) })

const person = data.person
const isEmployees = props.context === 'employees'
const archive = props.archive

// ---------- Время ----------
const now = useWallClockNow()
const today = computed(() => dayjs(now.value.format(DATE_KEY)))
const dateOnly = (value: string) => dayjs(value.slice(0, 10))

// ---------- ТУ ----------
const todayKey = computed(() => today.value.format(DATE_KEY))
// Строка списка не содержит fired_at/is_main: до ответа GET показывается первое известное ТУ.
const isComplete = computed(() => data.employments.value.every(item => item.complete))
const activeEmployments = computed(() => data.employments.value.filter(item => isActiveEmployment(item, todayKey.value)))
// Только просмотр: архив либо активных ТУ не осталось (увольнение наступило).
// Только просмотр: архив либо все ТУ уже завершены (будущие ТУ — не архив: сотрудник принят, но ещё не вышел).
const viewOnly = computed(() => archive || (
    isEmployees && isComplete.value && data.employments.value.length > 0
    && data.employments.value.every(item => isEndedEmployment(item, todayKey.value))
))
// Запись возможна только в активном досье Сотрудников; права ролей проверяются дополнительно.
const canWrite = computed(() => isEmployees && !viewOnly.value)

const selected = computed<DossierEmployment | null>(() => {
    const list = data.employments.value
    if (fixedEmploymentId) return list.find(item => item.id === fixedEmploymentId) ?? scheduleRow?.employment ?? null
    if (!isComplete.value) return list[0] ?? null
    return viewOnly.value ? pickArchiveEmployment(list) : pickMainEmployment(list, todayKey.value)
})
// «Ещё работает» — только информация: остальные активные ТУ, без выбора и переключения досье.
const otherEmployments = computed(() => (
    !isEmployees || viewOnly.value || !isComplete.value ? [] : activeEmployments.value.filter(item => item.id !== selected.value?.id)
))

// Начальный месяц: активные — текущий; архив — месяц увольнения выбранного ТУ, без даты — текущий.
const anchorDay = computed(() => (viewOnly.value && selected.value?.firedAt ? dateOnly(selected.value.firedAt) : today.value))
// Конец периода биржи: сегодня; у архива — конец месяца увольнения.
const exchangeEnd = computed(() => (viewOnly.value ? anchorDay.value.endOf('month') : today.value))
const summaryMonth = computed(() => anchorDay.value.startOf('month'))

const weekStart = ref<Dayjs>(weekStartOf(anchorDay.value))
const disciplinePeriod = ref<DisciplinePeriod>('month')
const disciplineMonth = ref<Dayjs>(summaryMonth.value)
const exchangePeriod = ref<ExchangePeriod>('3m')

// Смена отображаемого ТУ (ответ GET, увольнение основного) меняет все зависимые блоки и их периоды.
watch(() => selected.value?.id, (id, previous) => {
    if (!id || id === previous) return
    weekStart.value = weekStartOf(anchorDay.value)
    disciplinePeriod.value = 'month'
    disciplineMonth.value = summaryMonth.value
})

// Архив: дата увольнения становится известна после GET User — периоды переходят к её месяцу/неделе.
// У активных опора — сегодня: смена суток не сбрасывает выбранную неделю.
watch(() => anchorDay.value.format(DATE_KEY), () => {
    if (!viewOnly.value) return
    weekStart.value = weekStartOf(anchorDay.value)
    disciplineMonth.value = summaryMonth.value
})

// ---------- Шапка ----------
const avatarFailed = ref(false)
watch(() => person.value.avatar, () => { avatarFailed.value = false })
const displayName = computed(() => fullName(person.value) || DASH)
const initials = computed(() => `${person.value.lastName[0] ?? ''}${person.value.firstName[0] ?? ''}`.toUpperCase() || DASH)
const roleLine = computed(() => [selected.value?.positionName || DASH, selected.value?.departmentName || DASH].join(' · '))
const tenureLine = computed(() => {
    const employment = selected.value
    if (!employment?.hiredAt) return null
    const until = employment.firedAt ? dateOnly(employment.firedAt) : today.value
    const tenure = formatTenure(employment.hiredAt, until)
    const period = employment.firedAt
        ? `в компании с ${formatDate(employment.hiredAt)} по ${formatDate(employment.firedAt)}`
        : `в компании с ${formatDate(employment.hiredAt)}`
    return tenure ? `${period} · ${tenure}` : period
})
const backLabel = computed(() => shortName(person.value) || 'Назад')

// ---------- Вкладки ----------
const activeTab = ref<DossierTab>('summary')
const tabs = computed(() => {
    const list: { id: DossierTab, label: string }[] = [
        { id: 'summary', label: 'Сводка' },
        { id: 'shifts', label: 'Смены' },
        { id: 'discipline', label: 'Дисциплина' },
    ]
    if (data.permissions.value.viewExchange) list.push({ id: 'exchange', label: 'Биржа' })
    return list
})

const openDisciplineMonth = (month: Dayjs) => {
    disciplinePeriod.value = 'month'
    disciplineMonth.value = month
    activeTab.value = 'discipline'
}

// ---------- Меню (только активные Сотрудники) ----------
type MenuId = 'edit-user' | 'edit-employment' | 'smart' | 'password'
const isMenuOpen = ref(false)
const menuWrap = ref<HTMLElement | null>(null)

// Меню r2: «Добавить трудоустройство» и «Уволить» в меню нет — только кнопка списка и подэкран ТУ.
const menuItems = computed(() => {
    if (!isEmployees || viewOnly.value || data.userStatus.value !== 'ready' || !selected.value) return []
    const p = data.permissions.value
    const items: { id: MenuId, label: string, danger?: boolean, divider?: boolean }[] = []
    if (p.editUser) items.push({ id: 'edit-user', label: 'Изменить личные данные' })
    if (p.editEmployment || p.createEmployment) items.push({ id: 'edit-employment', label: 'Изменить трудоустройство' })
    if (p.changeSmart) items.push({ id: 'smart', label: 'СмартОтметка' })
    if (p.resetPassword) items.push({ id: 'password', label: 'Доступ и пароль' })
    return items
})

const screen = ref<DossierScreen>('dossier')
const editFocus = ref<EditFocus>('user')

const runMenu = (id: MenuId) => {
    isMenuOpen.value = false
    if (id === 'edit-user' || id === 'edit-employment' || id === 'smart') {
        editFocus.value = id === 'edit-user' ? 'user' : id === 'smart' ? 'smart' : 'employment'
        screen.value = 'personal'
    } else {
        screen.value = 'password'
    }
}

const onDocumentPointer = (event: PointerEvent) => {
    if (isMenuOpen.value && menuWrap.value && !menuWrap.value.contains(event.target as Node)) isMenuOpen.value = false
}

// Назад/Отмена — к досье с прежними вкладкой, ТУ и периодами; несохранённый draft отбрасывается.
const backToDossier = () => {
    screen.value = 'dossier'
}

const EDIT_SCREENS: DossierScreen[] = ['personal', 'employment', 'new-employment', 'password']
const personalMounted = computed(() => canWrite.value && (screen.value === 'personal' || screen.value === 'employment' || screen.value === 'new-employment'))

// Досье перешло в режим просмотра (например, уволено последнее ТУ прошедшей датой при открытой форме):
// экраны изменения закрываются, остаётся досье.
watch(viewOnly, (value) => {
    if (value && EDIT_SCREENS.includes(screen.value)) screen.value = 'dossier'
})
const returnToPersonal = () => {
    screen.value = viewOnly.value ? 'dossier' : 'personal'
}
const editingEmploymentId = ref<string | null>(null)
const editingEmployment = computed(() => data.employments.value.find(item => item.id === editingEmploymentId.value) ?? null)
const openEmployment = (id: string) => {
    editingEmploymentId.value = id
    screen.value = 'employment'
}

// ---------- После записи ----------
// Подтверждённая запись: кэш периодов сбрасывается, список страницы обновляется, User перечитывается.
// true — GET успешен; ошибка GET не означает несохранения и не приводит к повторной записи.
const afterWrite = async () => {
    data.invalidatePeriodData()
    emit('updated')
    return data.reloadUser()
}

const onPersonalSaved = (refreshed: boolean) => {
    toast.success(refreshed ? 'Сохранено' : 'Сохранено, но обновить данные не удалось')
    screen.value = 'dossier'
}

const onPasswordSaved = async () => {
    // reset-password может обновить email; доставка письма успехом не подтверждается.
    await data.reloadUser()
    screen.value = 'dossier'
}



onMounted(() => {
    document.addEventListener('pointerdown', onDocumentPointer)
    void data.reloadUser()
})

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocumentPointer)
})
</script>
